import React, {useMemo} from 'react';
import {Trans} from 'react-i18next';
import {Field} from 'formik';
import SelectBox from '../../../../components/formik/SelectBox';
import InputBox from '../../../../components/formik/InputBox';
import {getCurrencyOptions} from '../../../../utils/skincareUtils';
import _get from 'lodash/get';

export default function CurrencyPriceTable({
  formikProps,
  fieldName,
  priceType,
}) {
  const isVariablePrice = priceType === 'variable';
  const currencyOptions = useMemo(() => getCurrencyOptions(), []);

  const pricesArray = _get(formikProps.values, fieldName, []);

  const selectedCurrencies = useMemo(() => {
    return new Set(pricesArray.map(p => p.currency).filter(Boolean));
  }, [pricesArray]);

  const getAvailableOptions = (currentIndex) => {
    const currentCurrency = _get(pricesArray, `[${currentIndex}].currency`);
    return currencyOptions.filter(
      option => !selectedCurrencies.has(option.value) || option.value === currentCurrency
    );
  };

  const onAdd = () => {
    const newPrice = isVariablePrice
      ? {
          currency: '',
          lowestPrice: '',
          highestPrice: '',
        }
      : {
          currency: '',
          originalPrice: '',
          sellingPrice: '',
        };
    const updatedPrices = [...pricesArray, newPrice];
    formikProps.setFieldValue(fieldName, updatedPrices);
  };

  const onRemove = index => {
    const updatedPrices = pricesArray.filter((_, idx) => idx !== index);
    formikProps.setFieldValue(fieldName, updatedPrices);
  };

  return (
    <table
      className="table table-striped table-bordered table-hover dataTable"
      style={{textAlign: 'center'}}
    >
      <thead>
        <tr>
          <th>
            <Trans i18nKey="p.s.addoreditskincareproduct.price.table.currency">
              Currency
            </Trans>
          </th>
          <th>
            <Trans
              i18nKey={
                isVariablePrice
                  ? 'p.s.addoreditskincareproduct.price.table.lowest.price'
                  : 'p.s.addoreditskincareproduct.price.table.original.price'
              }
            >
              {isVariablePrice ? 'Lowest Price' : 'Original Price'}
            </Trans>
            <span className="required">*</span>
          </th>
          <th>
            <Trans
              i18nKey={
                isVariablePrice
                  ? 'p.s.addoreditskincareproduct.price.table.highest.price'
                  : 'p.s.addoreditskincareproduct.price.table.selling.price'
              }
            >
              {isVariablePrice ? 'Highest Price' : 'Selling Price'}
            </Trans>
          </th>
          <th>
            <Trans i18nKey="p.s.addoreditskincareproduct.price.table.action">
              Action
            </Trans>
          </th>
        </tr>
      </thead>
      <tbody>
        {pricesArray.map((_, index) => (
          <tr key={`price-row-${index}`}>
            <td className="col-md-3">
              <Field
                name={`${fieldName}[${index}].currency`}
                options={getAvailableOptions(index)}
                component={SelectBox}
              />
            </td>
            <td className="col-md-3">
              <Field
                name={`${fieldName}[${index}].${
                  isVariablePrice ? 'lowestPrice' : 'originalPrice'
                }`}
                type="number"
                min="0"
                step="0.01"
                decimalPlaces={2}
                component={InputBox}
              />
            </td>
            <td className="col-md-3">
              <Field
                name={`${fieldName}[${index}].${
                  isVariablePrice ? 'highestPrice' : 'sellingPrice'
                }`}
                type="number"
                min="0"
                step="0.01"
                decimalPlaces={2}
                component={InputBox}
              />
            </td>
            <td className="col-md-3">
              <div className="d-flex kt-margin-l-20">
                <div
                  className="text-primary kt-margin-r-20 cursor-pointer"
                  onClick={() => onAdd()}
                >
                  <i className="fa fa-plus-circle" />
                </div>
                {pricesArray.length > 1 && (
                  <div
                    className="text-primary cursor-pointer"
                    onClick={() => onRemove(index)}
                  >
                    <i className="fa fa-times-circle" />
                  </div>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
